import { useMemo, useState } from 'react';
import type { AdminTarget } from '@dunea/plugin-sdk';
import type { AdminExtensionView } from '@dunea/shared';
import { listAdminExtensionsCached } from '~/lib/api/admin-extensions';
import { classNames } from '~/utils/classNames';
import { CARD, CAPTION } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { ListingIcon } from '~/components/workbench/tools/marketplace/parts';
import { AdminExtensionFrame } from './AdminExtensionFrame';
import { composeSlot, type SlotEntry } from './admin-extensions-model';
import { adminCardsFor } from './registry';

/** Installed plugins' extensions for a target (shared, short-lived cache). Empty while loading or on error. */
export function useAdminExtensions(appId: string, target: AdminTarget): AdminExtensionView[] {
  const list = useResource(() => listAdminExtensionsCached(appId, target), `${appId}:${target}`);

  return list.data ?? [];
}

/** First-party cards + plugin extensions for a target, in display order. */
export function useSlotEntries(appId: string, target: AdminTarget): SlotEntry[] {
  const plugins = useAdminExtensions(appId, target);

  return useMemo(() => composeSlot(target, adminCardsFor(target), plugins), [target, plugins]);
}

/**
 * Admin extension blocks for a resource view: Dunea's own cards first
 * (registry.ts), then installed plugins' cards, each a sandboxed iframe. Renders
 * nothing when there is nothing to show, so views can mount it unconditionally.
 */
export function AdminExtensionSlot({
  appId,
  target,
  resourceId,
  className,
}: {
  appId: string;
  target: AdminTarget;
  resourceId?: string;
  className?: string;
}) {
  const entries = useSlotEntries(appId, target);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className={classNames('space-y-3', className)} data-admin-target={target}>
      {entries.map((entry) =>
        entry.source === 'first-party' ? (
          <entry.card.Component key={entry.key} appId={appId} target={target} resourceId={resourceId} />
        ) : (
          <PluginBlock key={entry.key} appId={appId} extension={entry.extension} resourceId={resourceId} />
        ),
      )}
    </div>
  );
}

function PluginBlock({
  appId,
  extension,
  resourceId,
}: {
  appId: string;
  extension: AdminExtensionView;
  resourceId?: string;
}) {
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <section className={classNames(CARD, 'overflow-hidden')} aria-label={`${extension.name}: ${extension.title}`}>
      <div className="flex items-center gap-2 border-b border-hairline px-3 py-2">
        <ListingIcon url={extension.iconUrl} name={extension.name} size={16} />
        <h4 className={classNames(CAPTION, 'min-w-0 truncate')}>{extension.title}</h4>
        <span className="min-w-0 truncate text-[11px] text-muted">{extension.name}</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="ui-btn-icon"
          title="Reload"
          aria-label={`Reload ${extension.name}`}
        >
          <div className="i-ph:arrow-clockwise text-[12px]" />
        </button>
      </div>
      <AdminExtensionFrame
        appId={appId}
        extension={extension}
        resourceId={resourceId}
        mode="block"
        reloadKey={reloadKey}
      />
    </section>
  );
}
